'use client';

import { safeLocalStorage } from '@/lib/safe-storage';
import { useCheckoutStore, CHECKOUT_STEPS, type CheckoutStep } from '@/stores/checkout-store';

/**
 * Старт входа через VK ID.
 *
 * Вынесено из auth-modal, потому что тот же запуск нужен странице
 * /auth/vk-callback — там кнопка «Повторить вход через VK» после отказа.
 */

const VK_APP_ID = process.env.NEXT_PUBLIC_VK_APP_ID || '54509489';

// Вход через VK уводит со страницы целиком, поэтому «куда вернуть человека»
// переживает перезагрузку только через localStorage: isOpen и currentStep
// корзины в persist не сохраняются и после возврата сбрасываются на первый шаг.
const RETURN_KEY = 'vk_auth_return';

// Метку не тащим дольше получаса: иначе давняя неудачная попытка открывала бы
// корзину при следующем — совсем другом — входе через VK.
const RETURN_TTL_MS = 30 * 60 * 1000;

interface VkReturnTarget {
  checkoutStep: CheckoutStep | null;
}

function generateRandomString(length: number): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-';
  const arr = new Uint8Array(length);
  crypto.getRandomValues(arr);
  return Array.from(arr, (b) => chars[b % chars.length]).join('');
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(hash)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

export async function startVkAuth(): Promise<void> {
  const redirectUri = `${window.location.origin}/auth/vk-callback`;
  const state = generateRandomString(40);
  const codeVerifier = generateRandomString(64);
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  // Сохраняем code_verifier и state для проверки на callback
  sessionStorage.setItem('vk_code_verifier', codeVerifier);
  sessionStorage.setItem('vk_state', state);

  // Метка возврата: если человек логинился прямо из корзины — вернём его в неё.
  // Когда корзина закрыта (повтор входа со страницы callback), ранее сохранённую
  // метку не затираем — иначе первая же неудача теряла бы контекст корзины.
  const checkout = useCheckoutStore.getState();
  if (checkout.isOpen) {
    safeLocalStorage.setItem(
      RETURN_KEY,
      JSON.stringify({ checkoutStep: checkout.currentStep, savedAt: Date.now() }),
    );
  }

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: VK_APP_ID,
    redirect_uri: redirectUri,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    scope: 'phone',
  });

  window.location.href = `https://id.vk.com/authorize?${params.toString()}`;
}

/** Читает и сразу стирает метку возврата — чтобы она не сработала во второй раз. */
export function consumeVkReturnTarget(): VkReturnTarget | null {
  const raw = safeLocalStorage.getItem(RETURN_KEY);
  safeLocalStorage.removeItem(RETURN_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as { checkoutStep?: unknown; savedAt?: unknown };
    const savedAt = typeof parsed?.savedAt === 'number' ? parsed.savedAt : 0;
    if (Date.now() - savedAt > RETURN_TTL_MS) return null;

    const step = parsed?.checkoutStep;
    const isKnownStep =
      typeof step === 'string' && CHECKOUT_STEPS.includes(step as CheckoutStep);
    return { checkoutStep: isKnownStep ? (step as CheckoutStep) : null };
  } catch {
    return null;
  }
}
